import React, {useEffect, useState} from 'react'
import {useLazyQuery, useQuery} from '@apollo/client'
import {Card, Col, Row, Spinner} from 'react-bootstrap'
import Select from 'react-select'
import {KTIcon} from '../../../../_metronic/helpers'
import {GET_EXECUTIVE_DASHBOARD_SUMMARY} from '../../gql/queries/reportsQuery'
import {GET_ALL_INSURANCE_OPERATORS_LIST} from '../../gql/queries/carriersQuery'
import {GET_ALL_USERS_FILTER} from '../../gql/queries/usersQuery'
import {ExecutiveTrendChart} from './ExecutiveTrendChart'
import {PortfolioGrowthChart} from './PortfolioGrowthChart'
import {NetGrowthWaterfallChart} from './NetGrowthWaterfallChart'
import {BreakdownKpiTable} from './BreakdownKpiTable'
import {CohortMatrixTable} from './CohortMatrixTable'
import {retentionTextColor, inverseTextColor, renovationTextColor, inverseStyle, retentionStyle} from './dashboardColors'
import {parseId, hasPermission, isAdministrator} from '../../helpers'
import {useGetUser} from '../../store/userStore'
import {ALL_PERMISSIONS} from '../../helpers/consts'
import {INITIAL_POLICY_FILTERS} from '../../helpers/consts'

// ─── Types ────────────────────────────────────────────────────────────────────

interface IPeriodData {
  period: string
  periodLabel: string
  policiesCount: number
  applicantsCount: number
  cancellationsCount: number
  atRiskCount: number
  neverStartedCount: number
  retention3mPct: number
  retention6mPct: number
  renovationsPct: number
}

interface ICohortMatrixRow {
  effectiveYear: number
  effectiveMonth: number
  monthLabel: string
  totalPolicies: number
  activeCounts: number[]  // index k = active at offset k (0–12)
}

interface ITenureBucket {
  label: string
  count: number
  pct: number
}

interface IBreakdownKpi {
  name: string
  newPolicies: number
  newPoliciesPrev: number | null
  cancellations: number
  atRiskCount: number | null
  neverStartedCount: number
  cancellationsPct: number
  churnRate: number
  retention3mPct: number
  retention6mPct: number
  renovationsPct: number
}

// ─── Risk panel ───────────────────────────────────────────────────────────────

interface IRiskSignal {
  severity: 'critical' | 'warning'
  group: string
  name: string
  reason: string
  value: string
  score: number
}

const medianCount = (rows: IBreakdownKpi[]) => {
  if (rows.length === 0) return 0
  const sorted = [...rows].map((r) => r.newPolicies ?? 0).sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]
}

const computeRisks = (
  carrierKpis: IBreakdownKpi[],
  agentKpis: IBreakdownKpi[],
  dateFrom: Date,
): IRiskSignal[] => {
  const signals: IRiskSignal[] = []

  // Months elapsed since the start of the selected period — used to gate
  // cohort retention signals that need enough runway to be meaningful.
  const now = new Date()
  const monthsElapsed = (now.getFullYear() - dateFrom.getFullYear()) * 12 + (now.getMonth() - dateFrom.getMonth())
  const can3m = monthsElapsed >= 3

  const check = (rows: IBreakdownKpi[], group: string) => {
    const threshold = medianCount(rows)  // only consider top 50% by volume
    rows.forEach((row) => {
      const np = row.newPolicies ?? 0
      if (np < threshold) return  // skip bottom half to avoid noise from small carriers/agents

      if (can3m) {
        const r3m = row.retention3mPct ?? 0
        if (r3m > 0 && r3m < 50) {
          signals.push({severity: 'critical', group, name: row.name, reason: 'Low 3-month retention', value: `${r3m.toFixed(1)}%`, score: 25 + (50 - r3m)})
        } else if (r3m > 0 && r3m < 65) {
          signals.push({severity: 'warning', group, name: row.name, reason: 'Below-target 3m retention', value: `${r3m.toFixed(1)}%`, score: 10 + (65 - r3m)})
        }
      }

      const netG = np - (row.cancellations ?? 0) - (row.neverStartedCount ?? 0)
      if (netG < 0) {
        signals.push({severity: 'warning', group, name: row.name, reason: 'Negative net growth', value: netG.toLocaleString(), score: 15 + Math.abs(netG / Math.max(np, 1) * 20)})
      }
    })
  }

  check(carrierKpis, 'Carrier')
  check(agentKpis, 'Agent')

  // deduplicate on (group, name, reason) keeping highest score
  const seen = new Map<string, IRiskSignal>()
  signals.forEach((s) => {
    const key = `${s.group}:${s.name}:${s.reason}`
    if (!seen.has(key) || s.score > seen.get(key)!.score) seen.set(key, s)
  })

  return Array.from(seen.values())
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
}

// ─── Tenure bell curve ────────────────────────────────────────────────────────

const TenureBellCurve = ({buckets}: {buckets: ITenureBucket[]}) => {
  if (buckets.length === 0) return null
  const W = 260; const H = 72
  const padL = 6; const padR = 6; const padT = 8; const padB = 18
  const innerW = W - padL - padR
  const innerH = H - padT - padB
  const maxCount = Math.max(...buckets.map((b) => b.count))
  if (maxCount === 0) return null

  const pts = buckets.map((b, i) => ({
    x: padL + (i / (buckets.length - 1)) * innerW,
    y: padT + innerH - (b.count / maxCount) * innerH,
    label: b.label,
    count: b.count,
    pct: b.pct,
  }))

  // Smooth cubic bezier through all points
  let linePath = `M ${pts[0].x} ${pts[0].y}`
  for (let i = 0; i < pts.length - 1; i++) {
    const cpx = (pts[i].x + pts[i + 1].x) / 2
    linePath += ` C ${cpx} ${pts[i].y}, ${cpx} ${pts[i + 1].y}, ${pts[i + 1].x} ${pts[i + 1].y}`
  }
  const fillPath = `${linePath} L ${pts[pts.length - 1].x} ${padT + innerH} L ${pts[0].x} ${padT + innerH} Z`

  const COLORS = ['#f64e60', '#ffa800', '#1bc5bd', '#50cd89']
  const shortLabel = (l: string) => l.replace(' yrs', '').replace(' yr', '')

  return (
    <svg width='100%' viewBox={`0 0 ${W} ${H}`} style={{display: 'block'}}>
      <defs>
        <linearGradient id='tenureCurveGrad' x1='0' y1='0' x2='1' y2='0'>
          {COLORS.map((c, i) => (
            <stop key={i} offset={`${(i / (COLORS.length - 1)) * 100}%`} stopColor={c} stopOpacity='0.25' />
          ))}
        </linearGradient>
        <linearGradient id='tenureCurveStroke' x1='0' y1='0' x2='1' y2='0'>
          {COLORS.map((c, i) => (
            <stop key={i} offset={`${(i / (COLORS.length - 1)) * 100}%`} stopColor={c} />
          ))}
        </linearGradient>
      </defs>

      {/* fill */}
      <path d={fillPath} fill='url(#tenureCurveGrad)' />
      {/* stroke */}
      <path d={linePath} fill='none' stroke='url(#tenureCurveStroke)' strokeWidth='2' strokeLinecap='round' />

      {/* dots + tooltips */}
      {pts.map((p, i) => (
        <g key={i}>
          <title>{p.label}: {p.count.toLocaleString()} ({p.pct}%)</title>
          <circle cx={p.x} cy={p.y} r={3.5} fill={COLORS[i]} stroke='#fff' strokeWidth='1.5' />
          <text x={p.x} y={padT + innerH + 13} textAnchor='middle' fontSize='8.5' fill='#a1a5b7'>
            {shortLabel(p.label)}
          </text>
        </g>
      ))}

      {/* baseline */}
      <line x1={padL} y1={padT + innerH} x2={W - padR} y2={padT + innerH} stroke='#e4e6ef' strokeWidth='1' />
    </svg>
  )
}

const RiskPanel = ({risks, loading}: {risks: IRiskSignal[]; loading: boolean}) => {
  if (loading || risks.length === 0) return null
  return (
    <Card className='card-flush mb-4'>
      <Card.Header className='border-0 pt-4 pb-2'>
        <h3 className='card-title'>
          <span className='card-label fw-bold fs-6'>
            <span className='me-2'>⚠</span>Top Risks This Period
          </span>
        </h3>
        <div className='card-toolbar'>
          <span className='text-muted fs-8'>auto-calculated · top 50% by volume per group</span>
        </div>
      </Card.Header>
      <Card.Body className='pt-0 pb-3'>
        <div className='d-flex flex-wrap gap-3'>
          {risks.map((r, i) => (
            <div
              key={i}
              className='d-flex align-items-start gap-3 p-3 rounded'
              style={{
                flex: '1 1 260px',
                minWidth: 220,
                backgroundColor: r.severity === 'critical' ? '#fff5f8' : '#fffbf0',
                border: `1px solid ${r.severity === 'critical' ? '#ffd6e0' : '#ffe8a1'}`,
              }}
            >
              <div
                className='rounded-circle d-flex align-items-center justify-content-center flex-shrink-0'
                style={{
                  width: 36, height: 36,
                  backgroundColor: r.severity === 'critical' ? '#f64e60' : '#ffa800',
                  color: '#fff', fontSize: '1rem', fontWeight: 700,
                }}
              >
                {i + 1}
              </div>
              <div>
                <div className='fw-bold text-dark fs-7'>{r.name}</div>
                <div className='text-muted fs-8 mb-1'>{r.group}</div>
                <div className='fs-7'>{r.reason}: <strong>{r.value}</strong></div>
                <span
                  className='badge mt-1'
                  style={{
                    backgroundColor: r.severity === 'critical' ? '#f64e60' : '#ffa800',
                    color: '#fff', fontSize: '0.65rem',
                  }}
                >
                  {r.severity}
                </span>
              </div>
            </div>
          ))}
        </div>
      </Card.Body>
    </Card>
  )
}

interface IPolicyDashboardSummary {
  totalPolicies: number
  totalEnrolled: number
  totalApplicants: number
  totalPoliciesLastPeriod: number
  totalApplicantsLastPeriod: number
  growthPercentage: number
  newClientsCount: number
  cancellationsCount: number
  atRiskCount: number
  churnRate: number
  newMembersCount: number
  lostMembersCount: number
  netMemberGrowth: number
  retention3mPct: number
  retention6mPct: number
  renovationsPct: number
  neverStartedCount: number
  avgTenureMonths: number
  tenureDistribution: ITenureBucket[]
  carrierKpis: IBreakdownKpi[]
  policyTypeKpis: IBreakdownKpi[]
  agentKpis: IBreakdownKpi[]
  advisorKpis: IBreakdownKpi[]
  periodData: IPeriodData[]
  cohortMatrix: ICohortMatrixRow[]
}

interface IResDashboard {
  policyDashboardSummary: IPolicyDashboardSummary
}

// ─── Month / Year filter ──────────────────────────────────────────────────────

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const getYearRange = (year: number): {dateFrom: Date; dateTo: Date} => ({
  dateFrom: new Date(year, 0, 1, 0, 0, 0, 0),
  dateTo: new Date(year, 11, 31, 23, 59, 59, 999),
})

const getMonthRange = (year: number, month: number): {dateFrom: Date; dateTo: Date} => ({
  dateFrom: new Date(year, month - 1, 1, 0, 0, 0, 0),
  dateTo: new Date(year, month, 0, 23, 59, 59, 999),
})

const buildYearButtons = (): number[] => {
  const y = new Date().getFullYear()
  return [y - 2, y - 1, y]
}

/** Default months: Jan through last completed month of the current year.
 *  If today is January, fall back to the previous year with all 12 months. */
const getDefaultMonths = (year: number): number[] => {
  const now = new Date()
  if (now.getFullYear() === year) {
    const lastCompleted = now.getMonth() // getMonth() is 0-based, so this equals last completed month (1-based)
    if (lastCompleted === 0) return [] // January → no completed months yet → full year
    return Array.from({length: lastCompleted}, (_, i) => i + 1)
  }
  return Array.from({length: 12}, (_, i) => i + 1) // prior/future year → all months
}

const getInitialYear = (): number => {
  const now = new Date()
  // If no months are completed yet in this year, default to previous year
  return now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
}

const formatPeriodLabel = (year: number, months: number[]): string => {
  if (months.length === 0) return `Full Year ${year}`
  if (months.length === 1) return `${MONTH_NAMES[months[0] - 1]} ${year}`
  const sorted = [...months].sort((a, b) => a - b)
  const first = MONTH_NAMES[sorted[0] - 1]
  const last = MONTH_NAMES[sorted[sorted.length - 1] - 1]
  return `${first} – ${last} ${year}`
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (date: Date) => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const formatNumber = (num: number) => new Intl.NumberFormat('en-US').format(num)

const formatPct = (num: number) => {
  const sign = num > 0 ? '+' : ''
  return `${sign}${num.toFixed(1)}%`
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const LiveKpiCard = ({
  icon,
  iconColor,
  title,
  value,
  subtitle,
  badge,
  badgeColor,
  badgeStyle,
  valueStyle,
  loading,
}: {
  icon: string
  iconColor: string
  title: string
  value: React.ReactNode
  subtitle?: string
  badge?: string
  badgeColor?: string
  badgeStyle?: React.CSSProperties
  valueStyle?: React.CSSProperties
  loading: boolean
}) => (
  <Card className='card-flush h-100'>
    <Card.Body className='d-flex flex-column py-3'>
      <div className='d-flex align-items-center mb-2'>
        <KTIcon iconName={icon} className={`fs-1 ${iconColor} me-2`} />
        <span className='text-gray-700 fw-bold fs-7'>{title}</span>
      </div>
      <div className='d-flex align-items-baseline'>
        <span className='fs-2x fw-bold me-2' style={valueStyle}>
          {loading ? <Spinner animation='border' size='sm' /> : value}
        </span>
      </div>
      {subtitle && (
        <div className='mt-2'>
          <span className='text-muted fs-8'>{subtitle}</span>
        </div>
      )}
      {badge && (
        <div className='mt-1'>
          {badgeStyle
            ? <span className='badge' style={badgeStyle}>{badge}</span>
            : <span className={`badge badge-light-${badgeColor ?? 'primary'}`}>{badge}</span>
          }
        </div>
      )}
    </Card.Body>
  </Card>
)

// ─── Page ─────────────────────────────────────────────────────────────────────

export const ExecutiveDashboardPage = () => {
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const permissions = useGetUser((s) => s.user?.permissions)
  const isAgente = userGroups.includes('Agente')
  const isSupervisor = userGroups.includes('Supervisor')
  const isAdmin = permissions ? isAdministrator(permissions) : false
  const hasViewAllPolicies = permissions
    ? hasPermission(permissions, ALL_PERMISSIONS.VIEW_ALL_POLICIES)
    : false
  const canSeeAdvisorPerformance = isSupervisor || isAdmin || hasViewAllPolicies
  const _initYear = getInitialYear()
  const [selectedYear, setSelectedYear] = useState<number>(_initYear)
  const [selectedMonths, setSelectedMonths] = useState<number[]>(getDefaultMonths(_initYear))

  // ── Temporary policy filters (for validation / comparison) ──────────────────
  const [carrierFilter, setCarrierFilter] = useState<{value: string; label: string} | null>(null)
  const [agentFilter, setAgentFilter] = useState<{value: string; label: string} | null>(null)

  const {data: dataCarriers} = useQuery(GET_ALL_INSURANCE_OPERATORS_LIST, {fetchPolicy: 'cache-first'})
  const {data: dataUsers} = useQuery(GET_ALL_USERS_FILTER, {
    variables: {requireNpn: true},
    fetchPolicy: 'cache-first',
  })

  const sortedMonths = [...selectedMonths].sort((a, b) => a - b)
  const {dateFrom, dateTo} = selectedMonths.length > 0
    ? {
        dateFrom: getMonthRange(selectedYear, sortedMonths[0]).dateFrom,
        dateTo: getMonthRange(selectedYear, sortedMonths[sortedMonths.length - 1]).dateTo,
      }
    : getYearRange(selectedYear)

  const dateFromStr = formatDate(dateFrom)
  const dateToStr = formatDate(dateTo)
  const rangeLabel = formatPeriodLabel(selectedYear, selectedMonths)

  // ── Data fetching ────────────────────────────────────────────────────────────
  const [getSummary, {data, loading}] = useLazyQuery<IResDashboard>(GET_EXECUTIVE_DASHBOARD_SUMMARY, {
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    getSummary({
      variables: {
        dateFrom: dateFromStr,
        dateTo: dateToStr,
        dateCriteria: 'effective',
        policyTypeName: ['ACA'],
        carrier: carrierFilter ? [parseId(carrierFilter.value).toString()] : undefined,
        agent: agentFilter ? [parseId(agentFilter.value).toString()] : undefined,
      },
    })
  }, [selectedYear, selectedMonths, carrierFilter, agentFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleYearClick = (y: number) => {
    setSelectedYear(y)
    setSelectedMonths(getDefaultMonths(y))
  }

  const handleMonthClick = (m: number) => {
    setSelectedMonths(prev =>
      prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]
    )
  }

  // ── Derived values ───────────────────────────────────────────────────────────
  const summary = data?.policyDashboardSummary
  const newPolicies = summary?.totalPolicies ?? 0
  const totalEnrolled = summary?.totalEnrolled ?? newPolicies  // all statuses — denominator for exec metrics
  const totalApplicants = summary?.totalApplicants ?? 0
  const cancellations = summary?.cancellationsCount ?? 0
  const atRiskCount = summary?.atRiskCount ?? 0

  const churnRate = summary?.churnRate ?? 0
  const newMembers = summary?.newMembersCount ?? 0
  const lostMembers = summary?.lostMembersCount ?? 0
  const netMemberGrowth = summary?.netMemberGrowth ?? 0
  const avgMembers = totalEnrolled > 0 ? (totalApplicants / totalEnrolled).toFixed(2) : '0.00'
  const cancellationsPctVal = totalEnrolled > 0 ? (cancellations / totalEnrolled) * 100 : 0
  const cancellationsPct = `${cancellationsPctVal.toFixed(1)}%`
  const growthPct = summary?.growthPercentage ?? 0
  const retention3m = summary?.retention3mPct ?? 0
  const retention6m = summary?.retention6mPct ?? 0
  const renovationsPct = summary?.renovationsPct ?? 0
  const carrierKpis = summary?.carrierKpis ?? []
  const policyTypeKpis = summary?.policyTypeKpis ?? []
  const agentKpis = summary?.agentKpis ?? []
  const advisorKpis = summary?.advisorKpis ?? []
  const cohortMatrix = summary?.cohortMatrix ?? []
  const neverStartedCount = summary?.neverStartedCount ?? 0
  const avgTenureMonths = summary?.avgTenureMonths ?? 0
  const tenureDistribution = summary?.tenureDistribution ?? []
  const atRiskPctVal = totalEnrolled > 0 ? (atRiskCount / totalEnrolled) * 100 : 0
  const atRiskPct = `${atRiskPctVal.toFixed(1)}%`
  const neverStartedPctVal = totalEnrolled > 0 ? (neverStartedCount / totalEnrolled) * 100 : 0
  const neverStartedPct = `${neverStartedPctVal.toFixed(1)}%`
  const netGrowth = totalEnrolled - cancellations - neverStartedCount
  const netGrowthPctVal = totalEnrolled > 0 ? (netGrowth / totalEnrolled) * 100 : 0
  const netGrowthPct = `${netGrowthPctVal.toFixed(1)}%`
  const risks = computeRisks(carrierKpis, agentKpis, dateFrom)

  const openPoliciesInNewTabWithFilters = (extraFilters?: Record<string, unknown>) => {
    const nextFilters = {
      ...INITIAL_POLICY_FILTERS,
      policyType: ['ACA'],
      effectiveDateAfter: dateFromStr,
      effectiveDateBefore: dateToStr,
      commissionPaymentStatus: '',
      carrier: carrierFilter ? [String(parseId(carrierFilter.value))] : '',
      user: agentFilter ? [String(parseId(agentFilter.value))] : '',
      ...extraFilters,
    }

    window.localStorage.setItem('policies-filters', JSON.stringify(nextFilters))
    window.localStorage.setItem('policies-selected-years', JSON.stringify([selectedYear]))
    window.open('/policies', '_blank', 'noopener,noreferrer')
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className='p-4'>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className='d-flex flex-column flex-md-row align-items-md-start justify-content-between gap-3 mb-3'>
        <div>
          <h2 className='fw-bold text-dark mb-1'>Executive Dashboard</h2>
          <span className='text-muted fs-7'>
            {rangeLabel} &nbsp;·&nbsp; Effective date &nbsp;·&nbsp; ACA only
          </span>
        </div>

        {/* ── Filters ────────────────────────────────────────────────────── */}
        <div className='d-flex flex-column gap-1 align-items-end'>

          {/* Row 1: Years | Carrier / Agent dropdowns */}
          <div className='d-flex flex-wrap align-items-center gap-2 justify-content-end'>
            {/* Year buttons */}
            <div className='d-flex gap-1'>
              {buildYearButtons().map((y) => (
                <button
                  key={y}
                  onClick={() => handleYearClick(y)}
                  className={`btn btn-sm ${selectedYear === y ? 'btn-primary' : 'btn-light-primary'}`}
                >
                  {y}
                </button>
              ))}
            </div>

            <div className='vr mx-1' />

            {/* Carrier / agent dropdowns */}
            <span className='text-muted fs-8'>Filter by:</span>
            <div style={{minWidth: 180}}>
              <Select
                placeholder='All carriers'
                isClearable
                value={carrierFilter}
                onChange={(v) => setCarrierFilter(v as any)}
                options={
                  dataCarriers?.allInsuranceOperators?.edges?.map(({node}: any) => ({
                    value: node.id,
                    label: node.name,
                  })) ?? []
                }
                styles={{control: (base) => ({...base, fontSize: '0.8rem'})}}
              />
            </div>
            {(!isAgente || hasViewAllPolicies) && (
              <div style={{minWidth: 200}}>
                <Select
                  placeholder='All agents'
                  isClearable
                  value={agentFilter}
                  onChange={(v) => setAgentFilter(v as any)}
                  options={
                    dataUsers?.allUsers?.edges?.map(({node}: any) => ({
                      value: node.id,
                      label: `${node.firstName} ${node.lastName} - ${node.email}${node.npn ? ` (${node.npn})` : ''}`,
                    })) ?? []
                  }
                  styles={{control: (base) => ({...base, fontSize: '0.8rem'})}}
                />
              </div>
            )}
            {(carrierFilter || agentFilter) && (
              <button
                className='btn btn-sm btn-light-warning'
                onClick={() => { setCarrierFilter(null); setAgentFilter(null) }}
                title='Clear filters'
              >
                ✕ Clear
              </button>
            )}
          </div>

          {/* Row 2: Month buttons (multi-select) */}
          <div className='d-flex flex-wrap gap-1 justify-content-end'>
            {MONTH_NAMES.map((name, idx) => {
              const m = idx + 1
              return (
                <button
                  key={m}
                  onClick={() => handleMonthClick(m)}
                  className={`btn btn-sm ${selectedMonths.includes(m) ? 'btn-primary' : 'btn-light-primary'}`}
                >
                  {name}
                </button>
              )
            })}
          </div>

        </div>
      </div>

      {/* ── KPI Row 1: Enrollment ───────────────────────────────────────────── */}
      <Row xs={1} sm={2} md={3} lg={6} className='g-3 mb-3'>
        <Col>
          <LiveKpiCard
            icon='shield-tick'
            iconColor='text-primary'
            title='New Enrollments'
            value={formatNumber(totalEnrolled)}
            subtitle={`${formatNumber(summary?.totalPoliciesLastPeriod ?? 0)} prior period · all statuses`}
            loading={loading}
          />
        </Col>
        <Col>
          <LiveKpiCard
            icon='cross-circle'
            iconColor='text-danger'
            title='Cancelled'
            value={loading ? '…' : (
              <span
                style={{cursor: 'pointer', textDecoration: 'underline dotted'}}
                onClick={() => openPoliciesInNewTabWithFilters({commissionPaymentStatus: 'CANCELED'})}
              >
                {formatNumber(cancellations)}
              </span>
            )}
            badge={`${cancellationsPct} of enrollments`}
            badgeStyle={inverseStyle(cancellationsPctVal)}
            subtitle='3+ month commission gap'
            loading={loading}
          />
        </Col>
        <Col>
          <LiveKpiCard
            icon='information-2'
            iconColor='text-warning'
            title='At Risk'
            value={loading ? '…' : (
              <span
                style={{cursor: 'pointer', textDecoration: 'underline dotted'}}
                onClick={() => openPoliciesInNewTabWithFilters({commissionPaymentStatus: 'AT_RISK'})}
              >
                {formatNumber(atRiskCount)}
              </span>
            )}
            badge={`${atRiskPct} of enrollments`}
            badgeStyle={inverseStyle(atRiskPctVal)}
            subtitle='2-month commission gap'
            loading={loading}
          />
        </Col>
        <Col>
          <LiveKpiCard
            icon='cross-circle'
            iconColor='text-warning'
            title='Never Started'
            value={loading ? '…' : (
              <span
                style={{cursor: 'pointer', textDecoration: 'underline dotted'}}
                onClick={() => openPoliciesInNewTabWithFilters({commissionPaymentStatus: 'NEVER_STARTED'})}
              >
                {formatNumber(neverStartedCount)}
              </span>
            )}
            badge={`${neverStartedPct} of enrollments`}
            badgeStyle={inverseStyle(neverStartedPctVal)}
            subtitle='no commission received'
            loading={loading}
          />
        </Col>
        <Col>
          <LiveKpiCard
            icon={netGrowth >= 0 ? 'arrow-up-right' : 'arrow-down-left'}
            iconColor={netGrowth >= 0 ? 'text-success' : 'text-danger'}
            title='Active Policies'
            value={loading ? '…' : formatNumber(netGrowth)}
            badge={`${netGrowthPct} of enrollments`}
            badgeStyle={retentionStyle(netGrowthPctVal) ?? {backgroundColor: '#f64e60', color: '#fff'}}
            subtitle='-cancelled -never started (at risk = active)'
            loading={loading}
          />
        </Col>
        <Col>
          <LiveKpiCard
            icon='none'
            iconColor={netMemberGrowth >= 0 ? 'text-success' : 'text-danger'}
            title='Net Member Growth'
            value={loading ? '…' : (netMemberGrowth >= 0 ? `+${formatNumber(netMemberGrowth)}` : formatNumber(netMemberGrowth))}
            subtitle={`+${formatNumber(newMembers)} new · −${formatNumber(lostMembers)} lost`}
            loading={loading}
          />
        </Col>
      </Row>

      {/* ── KPI Row 2: Performance ──────────────────────────────────────────── */}
      <Row xs={1} sm={2} md={3} lg={5} className='g-3 mb-3'>
        <Col>
          <LiveKpiCard
            icon='time'
            iconColor='text-info'
            title='Avg Customer Tenure'
            value={loading ? '…' : avgTenureMonths > 0 ? `${(avgTenureMonths / 12).toFixed(1)} yrs` : '—'}
            subtitle='active customers'
            loading={loading}
          />
        </Col>
        <Col>
          <LiveKpiCard
            icon='abstract-26'
            iconColor={churnRate <= 5 ? 'text-success' : churnRate <= 10 ? 'text-warning' : 'text-danger'}
            title='Churn Rate %'
            value={loading ? '…' : `${churnRate.toFixed(1)}%`}
            subtitle='activated policies cancelled before December'
            valueStyle={{color: inverseTextColor(churnRate)}}
            loading={loading}
          />
        </Col>
        <Col>
          <LiveKpiCard
            icon='arrows-loop'
            iconColor='text-success'
            title='% Renovations'
            value={loading ? '…' : `${renovationsPct.toFixed(1)}%`}
            subtitle='policies with RENEWED status'
            valueStyle={{color: renovationTextColor(renovationsPct)}}
            loading={loading}
          />
        </Col>
        <Col>
          <LiveKpiCard
            icon='shield-tick'
            iconColor='text-success'
            title='Cohort 3m'
            value={loading ? '…' : `${retention3m.toFixed(1)}%`}
            subtitle='active 3M after enrollment'
            valueStyle={{color: retentionTextColor(retention3m)}}
            loading={loading}
          />
        </Col>
        <Col>
          <LiveKpiCard
            icon='shield-tick'
            iconColor='text-primary'
            title='Cohort 6m'
            value={loading ? '…' : `${retention6m.toFixed(1)}%`}
            subtitle='active 6M after enrollment'
            valueStyle={{color: retentionTextColor(retention6m)}}
            loading={loading}
          />
        </Col>
      </Row>

      {/* ── Trend Chart + Period Table ───────────────────────────────────────── */}
      <Row className='g-3 mb-3'>
        <Col sm={12} lg={6}>
          <ExecutiveTrendChart
            periodData={summary?.periodData ?? []}
            loading={loading}
          />
        </Col>
        <Col sm={12} lg={6}>
          <Card className='card-flush h-100'>
            <Card.Header className='border-0 pt-4 pb-2'>
              <h3 className='card-title'>
                <span className='card-label fw-bold fs-6'>Enrollment by Period</span>
              </h3>
            </Card.Header>
            <Card.Body className='pt-0 pb-0'>
              <div className='table-responsive'>
                <table className='table table-hover table-sm align-middle fs-8 mb-0'>
                  <thead>
                    <tr className='text-muted fw-bold text-uppercase fs-8'>
                      <th>Period</th>
                      <th className='text-end'>New</th>
                      <th className='text-end'>Cancelled</th>
                      <th className='text-end'>At Risk</th>
                      <th className='text-end'>Never Started</th>
                      <th className='text-end'>Active</th>
                      <th className='text-end'>Cohort 3m</th>
                      <th className='text-end'>Cohort 6m</th>
                      <th className='text-end'>Renewals%</th>
                      <th className='text-end'>Cancels%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={10} className='text-center py-3'><Spinner animation='border' size='sm' /></td></tr>
                    ) : (summary?.periodData ?? []).length === 0 ? (
                      <tr><td colSpan={10} className='text-center text-muted py-3'>No data</td></tr>
                    ) : (
                      (summary?.periodData ?? []).map((row) => {
                        const newP = row.policiesCount ?? 0
                        const cancels = row.cancellationsCount ?? 0
                        const atRisk = row.atRiskCount ?? 0
                        const neverStarted = row.neverStartedCount ?? 0
                        const active = newP - cancels - neverStarted  // at risk stays active
                        const cancelsPct = newP > 0 ? (cancels / newP * 100) : 0
                        return (
                          <tr key={row.period}>
                            <td className='fw-semibold'>{row.periodLabel}</td>
                            <td className='text-end'>{newP.toLocaleString()}</td>
                            <td className='text-end fw-semibold' style={{color: '#f64e60'}}>{cancels.toLocaleString()}</td>
                            <td className='text-end fw-semibold' style={{color: '#ffa800'}}>{atRisk.toLocaleString()}</td>
                            <td className='text-end fw-semibold' style={{color: '#a1a5b7'}}>{neverStarted.toLocaleString()}</td>
                            <td className='text-end fw-semibold' style={{color: active >= 0 ? '#1bc5bd' : '#f64e60'}}>
                              {active.toLocaleString()}
                            </td>
                            <td className='text-end'><span style={{color: retentionTextColor(row.retention3mPct ?? 0), fontWeight: 600}}>{(row.retention3mPct ?? 0).toFixed(1)}%</span></td>
                            <td className='text-end'><span style={{color: retentionTextColor(row.retention6mPct ?? 0), fontWeight: 600}}>{(row.retention6mPct ?? 0).toFixed(1)}%</span></td>
                            <td className='text-end'><span style={{color: renovationTextColor(row.renovationsPct ?? 0), fontWeight: 600}}>{(row.renovationsPct ?? 0).toFixed(1)}%</span></td>
                            <td className='text-end'><span style={{color: inverseTextColor(cancelsPct), fontWeight: 600}}>{cancelsPct.toFixed(1)}%</span></td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── Retention Trend + Net Growth Waterfall ──────────────────────────── */}
      <Row className='g-3 mb-3'>
        <Col sm={12} lg={6}>
          <PortfolioGrowthChart
            periodData={summary?.periodData ?? []}
            loading={loading}
          />
        </Col>
        <Col sm={12} lg={6}>
          <NetGrowthWaterfallChart
            totalEnrolled={totalEnrolled}
            cancellations={cancellations}
            neverStarted={neverStartedCount}
            loading={loading}
          />
        </Col>
      </Row>

      {/* ── Cohort Retention Matrix + Tenure Distribution ───────────────────── */}
      {(cohortMatrix.length > 0 || tenureDistribution.length > 0 || loading) && (
        <Row className='g-3 mb-3'>
          <Col sm={12} lg={8}>
            <CohortMatrixTable rows={cohortMatrix} loading={loading} compact />
          </Col>
          <Col sm={12} lg={4}>
            <Card className='card-flush h-100'>
              <Card.Header className='border-0 pt-4 pb-2'>
                <h3 className='card-title'>
                  <span className='card-label fw-bold fs-6'>Customer Tenure</span>
                </h3>
                <div className='card-toolbar'>
                  <span className='text-muted fs-8'>bases on active clients</span>
                </div>
              </Card.Header>
              <Card.Body className='pt-2 pb-4'>
                {loading ? (
                  <div className='d-flex justify-content-center pt-4'>
                    <Spinner animation='border' size='sm' />
                  </div>
                ) : tenureDistribution.length === 0 ? (
                  <span className='text-muted fs-8'>No data</span>
                ) : (
                  <>
                    <div className='d-flex flex-column gap-4 pt-2'>
                      {tenureDistribution.map((b, i) => {
                        const color = ['#f64e60', '#ffa800', '#1bc5bd', '#50cd89'][i]
                        return (
                          <div key={b.label}>
                            <div className='d-flex justify-content-between align-items-center mb-1'>
                              <div className='d-flex align-items-center gap-2'>
                                <div style={{width: 10, height: 10, borderRadius: 2, backgroundColor: color, flexShrink: 0}} />
                                <span className='fs-7 fw-semibold text-gray-700'>{b.label}</span>
                              </div>
                              <div className='d-flex align-items-center gap-3'>
                                <span className='fs-7 fw-bold text-dark'>{b.count.toLocaleString()}</span>
                                <span className='text-muted fs-8' style={{minWidth: 36, textAlign: 'right'}}>{b.pct}%</span>
                              </div>
                            </div>
                            <div style={{height: 16, borderRadius: 6, backgroundColor: '#f1f3f7'}}>
                              <div style={{
                                height: '100%',
                                borderRadius: 6,
                                width: `${b.pct}%`,
                                backgroundColor: color,
                                transition: 'width 0.4s ease',
                              }} />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    <hr className='my-3' style={{borderColor: '#f1f3f7'}} />
                    <div className='text-muted fs-8 mb-1'>Distribution curve</div>
                    <TenureBellCurve buckets={tenureDistribution} />
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* ── Risk Panel ─────────────────────────────────────────────────────── */}
      <RiskPanel risks={risks} loading={loading} />

      {/* ── KPIs by Carrier (full-width) ──────────────────────────────────── */}
      {carrierKpis.length > 0 && (
        <Row className='g-4 mt-2'>
          <Col sm={12}>
            <BreakdownKpiTable
              title='KPIs by Carrier'
              groupLabel='Carrier'
              rows={carrierKpis}
              loading={loading}
            />
          </Col>
        </Row>
      )}

      {/* ── KPIs by Agent (full-width, commission-based) ──────────────────── */}
      {agentKpis.length > 0 && (
        <Row className='g-4 mt-2'>
          <Col sm={12}>
            <BreakdownKpiTable
              title='KPIs by Agent'
              groupLabel='Agent'
              rows={agentKpis}
              loading={loading}
            />
          </Col>
        </Row>
      )}

      {canSeeAdvisorPerformance && advisorKpis.length > 0 && (
        <Row className='g-4 mt-2'>
          <Col sm={12}>
            <BreakdownKpiTable
              title='KPIs by Advisor'
              groupLabel='Advisor'
              rows={advisorKpis}
              loading={loading}
            />
          </Col>
        </Row>
      )}


    </div>
  )
}
